import { useEffect, useMemo, useRef, useState } from 'react';
import { AppNode, Edge, ProcessDefinition, Swimlane } from '../types';
import { validateDiagram, summarize } from '../utils/validation';
import type { ValidationIssue } from '../utils/validation';

interface UseValidationArgs {
  processes: ProcessDefinition[];
  activeProcessId: string;
  nodes: AppNode[];
  edges: Edge[];
  swimlanes?: Swimlane[];
  /** Debounce delay for re-running rules on edits. Default 400ms. */
  debounceMs?: number;
}

const filterToActiveProcess = (all: ValidationIssue[], activeProcessId: string): ValidationIssue[] =>
  // Show issues tied to the active process plus diagram-wide issues that
  // have no processId set — cross-process issues still belong to the
  // current view because they reflect the whole file.
  all.filter(i => !i.processId || i.processId === activeProcessId);

export interface UseValidationResult {
  /** Issues scoped to the active process (plus diagram-wide issues with no processId). */
  issues: ValidationIssue[];
  /** Unfiltered issues across every process — used by export/save gates. */
  allIssues: ValidationIssue[];
  errors: number;
  warnings: number;
  /** Per-process error/warning counts — drives cross-process badges in UI. */
  processCounts: Record<string, { errors: number; warnings: number }>;
  isValidating: boolean;
  /** Force an immediate re-run (bypasses debounce). */
  revalidate: () => void;
}

/**
 * Live validation of the current diagram. Runs `validateDiagram` after a
 * short debounce on each change to avoid thrashing while the user types.
 *
 * The active process's in-editor state (nodes/edges/swimlanes) is merged
 * into the processes array before validation — the processes snapshot is
 * only updated when the user switches processes, so live edits would
 * otherwise be invisible to rules.
 */
export const useValidation = ({
  processes,
  activeProcessId,
  nodes,
  edges,
  swimlanes,
  debounceMs = 400,
}: UseValidationArgs): UseValidationResult => {
  const [allIssues, setAllIssues] = useState<ValidationIssue[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const timerRef = useRef<number | null>(null);

  const mergedProcesses = useMemo(
    () => processes.map(p => (p.id === activeProcessId ? { ...p, nodes, edges, swimlanes: swimlanes ?? p.swimlanes } : p)),
    [processes, activeProcessId, nodes, edges, swimlanes]
  );

  const run = useMemo(
    () => () => {
      setIsValidating(true);
      try {
        const next = validateDiagram(mergedProcesses);
        setAllIssues(next);
      } catch (err) {
        // A crashing rule must not freeze the editor. Surface via console so
        // the failing rule is discoverable, then reset issues to an empty list
        // so stale validation state doesn't mislead the UI.
        console.error('[useValidation] validation crashed:', err);
        setAllIssues([]);
      } finally {
        setIsValidating(false);
      }
    },
    [mergedProcesses]
  );

  useEffect(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(run, debounceMs);
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [run, debounceMs]);

  const issues = useMemo(() => filterToActiveProcess(allIssues, activeProcessId), [allIssues, activeProcessId]);
  const { errors, warnings } = useMemo(() => summarize(issues), [issues]);

  // Roll up per-process counts. Issues without a processId are diagram-wide
  // and attributed to every process so cross-cutting problems (e.g. missing
  // subprocess reference) surface on every row.
  const processCounts = useMemo(() => {
    const map: Record<string, { errors: number; warnings: number }> = {};
    for (const p of processes) map[p.id] = { errors: 0, warnings: 0 };
    for (const i of allIssues) {
      const targets = i.processId ? [i.processId] : processes.map(p => p.id);
      for (const id of targets) {
        if (!map[id]) map[id] = { errors: 0, warnings: 0 };
        if (i.severity === 'error') map[id].errors++;
        else map[id].warnings++;
      }
    }
    return map;
  }, [allIssues, processes]);

  return { issues, allIssues, errors, warnings, processCounts, isValidating, revalidate: run };
};
